import { z } from "zod";
import { AiProviderError } from "../provider.js";
import type {
  AiSuggestion,
  ModelStamp,
  SuggestionSet,
  SuggestionSetKind,
} from "../suggestion.js";
import { nameplateOcrProposalSchema, type NameplateOcrProposal } from "./nameplate-ocr.js";
import { panelScanProposalSchema, type PanelScanProposal } from "./panel-scan.js";
import {
  roomDeviceScanProposalSchema,
  type RoomDeviceScanProposal,
} from "./room-device-scan.js";

/**
 * Phase 8b — the single seam where AI response wire bytes become a
 * typed `SuggestionSet`. Every provider implementation (real or fake)
 * routes through `parseAiScanResponse`; unsupported responses are
 * rejected here, not deep in the domain, per the ImplementationPlan
 * test.
 *
 * One pipeline, multiple schemas: the dispatcher branches on `kind` to
 * pick the right per-suggestion validator. Adding R-AI-3 / R-AI-4
 * (Phase 8d) appends an entry to `PROPOSAL_SCHEMAS`; nothing else in
 * this file changes.
 *
 * Wire shape:
 *   {
 *     id, kind, modelStamp, timestamp, sourcePhotoIds,
 *     suggestions: [
 *       { id, confidence, sourcePhotoIds, proposedValue },
 *       ...
 *     ]
 *   }
 *
 * The wire shape is intentionally the same as the in-memory
 * `SuggestionSet<T>` shape — once the per-kind validator passes, the
 * parsed object *is* the suggestion set with no further translation.
 * Determinism: arrays preserve emission order so the producer-order
 * iteration contract (R-AI-2a) holds end-to-end.
 */

const modelStampSchema: z.ZodType<ModelStamp> = z.object({
  provider: z.string().min(1),
  model: z.string().min(1),
  version: z.string().min(1),
});

const suggestionSetKindSchema: z.ZodType<SuggestionSetKind> = z.enum([
  "panel-scan",
  "room-device-scan",
  "nameplate-ocr",
]);

/**
 * A normalized bounding box (R-SUPPORT) — fractions of the photo's own
 * dimensions, every field in `[0, 1]`. Rejected at the envelope seam if
 * the model emits out-of-range coordinates, same as any other wire
 * violation.
 */
const boundingBoxSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().min(0).max(1),
  height: z.number().min(0).max(1),
});

const suggestionRegionSchema = z.object({
  photoId: z.string().min(1),
  box: boundingBoxSchema,
  // Phase 16 — optional tile provenance (16a type; emitted by the tiling read
  // pass in 16d). When the read came from a system-generated 768 tile this
  // names the tile and carries the box in **tile** space. Optional, so
  // pre-Phase-16 cassettes and whole-photo reads still parse unchanged.
  tile: z
    .object({ tileKey: z.string().min(1), box: boundingBoxSchema })
    .optional(),
});

/**
 * The umbrella suggestion wire shape with `proposedValue` left as
 * `unknown` — the dispatcher validates each suggestion's
 * `proposedValue` against the kind-specific schema in a second pass so
 * the error message names the offending kind.
 *
 * `regions` is optional: responses recorded before the bounding-box
 * prompt (and any model that omits it) still parse.
 */
const aiSuggestionWireSchema = z.object({
  id: z.string().min(1),
  confidence: z.number().min(0).max(1),
  sourcePhotoIds: z.array(z.string().min(1)),
  regions: z.array(suggestionRegionSchema).optional(),
  proposedValue: z.unknown(),
});

const aiScanResponseWireSchema = z.object({
  id: z.string().min(1),
  kind: suggestionSetKindSchema,
  modelStamp: modelStampSchema,
  timestamp: z.string().min(1),
  sourcePhotoIds: z.array(z.string().min(1)),
  suggestions: z.array(aiSuggestionWireSchema),
});

export type AiScanResponseWire = z.infer<typeof aiScanResponseWireSchema>;

/**
 * Map from each `SuggestionSetKind` to the zod schema its suggestions'
 * `proposedValue` field must satisfy. Phase 8b registered `panel-scan`;
 * Phase 8d adds `room-device-scan` (R-AI-3) and `nameplate-ocr`
 * (R-AI-4). The map intentionally uses `Partial` rather than enforcing
 * every key — an unregistered kind raises `UNSUPPORTED_KIND` at the
 * dispatcher, which is the correct surfacing for a future R-AI-5
 * (voice) / R-AI-7 (safety flags) kind that lands before its schema
 * does.
 */
export const PROPOSAL_SCHEMAS: Partial<
  Record<SuggestionSetKind, z.ZodTypeAny>
> = {
  "panel-scan": panelScanProposalSchema,
  "room-device-scan": roomDeviceScanProposalSchema,
  "nameplate-ocr": nameplateOcrProposalSchema,
};

/**
 * Map of typed proposal values per kind, used to name the inferred
 * `T` of `SuggestionSet<T>` for callers that know which kind they
 * asked for.
 */
export type ProposalFor<K extends SuggestionSetKind> = K extends "panel-scan"
  ? PanelScanProposal
  : K extends "room-device-scan"
    ? RoomDeviceScanProposal
    : K extends "nameplate-ocr"
      ? NameplateOcrProposal
      : unknown;

/**
 * Validate one AI wire response and return the corresponding
 * `SuggestionSet`. Throws `AiProviderError(INVALID_RESPONSE)` on a
 * malformed envelope or any per-suggestion `proposedValue` that fails
 * the kind-specific schema; throws `AiProviderError(UNSUPPORTED_KIND)`
 * when the wire's `kind` is structurally legal but no schema is
 * registered for it yet.
 *
 * The optional `expectedKind` argument is the caller's stated
 * expectation (typically `request.kind`); a mismatch surfaces as
 * `KIND_MISMATCH` so the provider bug — "I asked for panel-scan and
 * got nameplate-ocr" — is distinct from a wire-validation failure.
 */
export function parseAiScanResponse(
  raw: unknown,
  expectedKind?: SuggestionSetKind,
): SuggestionSet<unknown> {
  const parsed = aiScanResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new AiProviderError(
      "INVALID_RESPONSE",
      `AI scan response failed envelope validation: ${parsed.error.message}`,
    );
  }
  const wire = parsed.data;

  if (expectedKind !== undefined && wire.kind !== expectedKind) {
    throw new AiProviderError(
      "KIND_MISMATCH",
      `AI scan response kind "${wire.kind}" does not match the request's ` +
        `expected kind "${expectedKind}".`,
    );
  }

  const proposalSchema = PROPOSAL_SCHEMAS[wire.kind];
  if (proposalSchema === undefined) {
    throw new AiProviderError(
      "UNSUPPORTED_KIND",
      `AI scan response kind "${wire.kind}" has no registered proposal ` +
        `schema. Phase 8b registered "panel-scan"; Phase 8d added ` +
        `"room-device-scan" and "nameplate-ocr". A kind reaching this ` +
        `branch is a future R-AI-5 / R-AI-7 wire that landed before its ` +
        `schema.`,
    );
  }

  const suggestions: AiSuggestion<unknown>[] = wire.suggestions.map(
    (s, index) => {
      const proposalParsed = proposalSchema.safeParse(s.proposedValue);
      if (!proposalParsed.success) {
        throw new AiProviderError(
          "INVALID_RESPONSE",
          `AI scan response suggestion #${index} (id "${s.id}") for kind ` +
            `"${wire.kind}" failed proposal validation: ${proposalParsed.error.message}`,
        );
      }
      return {
        id: s.id,
        proposedValue: proposalParsed.data as unknown,
        confidence: s.confidence,
        sourcePhotoIds: [...s.sourcePhotoIds],
        ...(s.regions !== undefined
          ? {
              regions: s.regions.map((r) => ({
                photoId: r.photoId,
                box: { ...r.box },
                ...(r.tile !== undefined
                  ? { tile: { tileKey: r.tile.tileKey, box: { ...r.tile.box } } }
                  : {}),
              })),
            }
          : {}),
        modelStamp: wire.modelStamp,
        timestamp: wire.timestamp,
      };
    },
  );

  return {
    id: wire.id,
    kind: wire.kind,
    suggestions,
    sourcePhotoIds: [...wire.sourcePhotoIds],
    modelStamp: wire.modelStamp,
    timestamp: wire.timestamp,
  };
}
